document.addEventListener("DOMContentLoaded", () => {
  if (customElements.get("full-screen-modal")) return;

  class FullScreenModal extends HTMLElement {
    constructor() {
      super();
      this.contentEl = null;
      this.triggerEl = null;
      this.closeEls = [];
      this.maskEl = null;
      this.lastFocusedEl = null;
      this.lastTabDirection = "forward";

      this.handleOpen = this.handleOpen.bind(this);
      this.handleClose = this.handleClose.bind(this);
      this.handleTriggerKeydown = this.handleTriggerKeydown.bind(this);
      this.handleCloseKeydown = this.handleCloseKeydown.bind(this);
      this.handleMaskClick = this.handleMaskClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
      this.stopPropagation = this.stopPropagation.bind(this);
    }

    connectedCallback() {
      this.initElements();
      this.moveContentToBody();
      this.bindEvents();
    }

    disconnectedCallback() {
      this.unbindEvents();
    }

    initElements() {
      this.contentEl = this.querySelector(".content");
      this.triggerEl = this.querySelector(".trigger");

      if (!this.contentEl) return;

      this.closeEls = this.contentEl.querySelectorAll(".close");
      this.maskEl = this.contentEl.querySelector(".mask");
      this.setupTriggerA11y();
      this.setupCloseA11y();
    }

    moveContentToBody() {
      if (!this.contentEl) return;

      if (this.contentEl.__moved) return;

      this.contentEl.__moved = true;
      this.contentEl.classList.add("image-preview-root");
      this.contentEl.style.display = "none";
      if (!this.contentEl.id) {
        this.contentEl.id = `full-screen-modal-${Math.random().toString(36).slice(2, 10)}`;
      }
      this.setupDialogA11y();
      if (!this.contentEl.hasAttribute("tabindex")) {
        this.contentEl.setAttribute("tabindex", "-1");
      }

      document.body.appendChild(this.contentEl);
    }

    bindEvents() {
      this.triggerEl?.addEventListener("click", this.handleOpen);
      this.triggerEl?.addEventListener("keydown", this.handleTriggerKeydown);

      this.closeEls.forEach((el) => {
        el.addEventListener("click", this.handleClose);
        el.addEventListener("keydown", this.handleCloseKeydown);
      });

      if (this.maskEl) {
        this.maskEl.addEventListener("click", this.handleMaskClick);

        // 阻止 mask 内部点击冒泡
        this.maskEl.querySelectorAll("*").forEach((el) => {
          el.addEventListener("click", this.stopPropagation);
        });
      }
    }

    unbindEvents() {
      this.triggerEl?.removeEventListener("click", this.handleOpen);
      this.triggerEl?.removeEventListener("keydown", this.handleTriggerKeydown);
      document.removeEventListener("keydown", this.handleKeydown);
      document.removeEventListener("focusin", this.handleFocusIn);

      this.closeEls.forEach((el) => {
        el.removeEventListener("click", this.handleClose);
        el.removeEventListener("keydown", this.handleCloseKeydown);
      });

      if (this.maskEl) {
        this.maskEl.removeEventListener("click", this.handleMaskClick);

        this.maskEl.querySelectorAll("*").forEach((el) => {
          el.removeEventListener("click", this.stopPropagation);
        });
      }
    }

    handleOpen() {
      if (!this.contentEl) return;

      this.lastFocusedEl = document.activeElement;
      this.contentEl.style.display = "block";
      requestAnimationFrame(() => {
        this.contentEl.classList.add("is-open");
      });
      this.contentEl.setAttribute("aria-hidden", "false");
      this.triggerEl?.setAttribute("aria-expanded", "true");
      this.focusModal();
      document.addEventListener("keydown", this.handleKeydown);
      document.addEventListener("focusin", this.handleFocusIn);

      document.body.style.overflow = "hidden";

      window.dispatchEvent(
        new CustomEvent("full-screen-modal-open", {
          detail: {
            contentEl: this.contentEl,
          },
        })
      );
    }

    handleClose() {
      if (!this.contentEl) return;

      this.contentEl.classList.remove("is-open");

      this.contentEl.style.display = "none";
      this.contentEl.setAttribute("aria-hidden", "true");
      document.removeEventListener("keydown", this.handleKeydown);
      document.removeEventListener("focusin", this.handleFocusIn);
      this.triggerEl?.setAttribute("aria-expanded", "false");

      document.body.style.overflow = "";

      const focusTarget =
        this.triggerEl ||
        (this.lastFocusedEl instanceof HTMLElement ? this.lastFocusedEl : null);
      focusTarget?.focus?.();

      window.dispatchEvent(
        new CustomEvent("full-screen-modal-close", {
          detail: {
            contentEl: this.contentEl,
          },
        })
      );
    }

    handleMaskClick(e) {
      // 只在点击 mask 自身时关闭
      if (e.target === this.maskEl) {
        this.handleClose();
      }
    }

    handleTriggerKeydown(e) {
      if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
        e.preventDefault();
        this.handleOpen();
      }
    }

    handleCloseKeydown(e) {
      if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
        e.preventDefault();
        this.handleClose();
      }
    }

    setupTriggerA11y() {
      if (!this.triggerEl || !this.contentEl) return;

      const tag = this.triggerEl.tagName.toLowerCase();
      const isNativeControl = ["button", "a", "input", "select", "textarea"].includes(tag);

      if (!isNativeControl && !this.triggerEl.hasAttribute("role")) {
        this.triggerEl.setAttribute("role", "button");
      }

      if (!isNativeControl && !this.triggerEl.hasAttribute("tabindex")) {
        this.triggerEl.setAttribute("tabindex", "0");
      }

      this.triggerEl.setAttribute("aria-haspopup", "dialog");
      this.triggerEl.setAttribute("aria-expanded", "false");
      if (this.contentEl.id) {
        this.triggerEl.setAttribute("aria-controls", this.contentEl.id);
      }
    }

    setupDialogA11y() {
      if (!this.contentEl) return;

      this.contentEl.setAttribute("role", "dialog");
      this.contentEl.setAttribute("aria-modal", "true");
      this.contentEl.setAttribute("aria-hidden", "true");

      const titleEl = this.contentEl.querySelector("h1, h2, h3, [data-modal-title]");
      if (titleEl) {
        if (!titleEl.id) {
          titleEl.id = `${this.contentEl.id}-title`;
        }
        this.contentEl.setAttribute("aria-labelledby", titleEl.id);
      } else if (!this.contentEl.getAttribute("aria-label")) {
        this.contentEl.setAttribute("aria-label", "Dialog");
      }
    }

    setupCloseA11y() {
      if (!this.closeEls?.length) return;

      this.closeEls.forEach((el) => {
        const tag = el.tagName.toLowerCase();
        const isNativeControl = ["button", "a", "input", "select", "textarea"].includes(tag);

        if (!isNativeControl && !el.hasAttribute("role")) {
          el.setAttribute("role", "button");
        }

        if (!isNativeControl && !el.hasAttribute("tabindex")) {
          el.setAttribute("tabindex", "0");
        }

        if (tag === "button" && !el.hasAttribute("type")) {
          el.setAttribute("type", "button");
        }

        if (!el.hasAttribute("aria-label")) {
          el.setAttribute("aria-label", "Close dialog");
        }
      });
    }

    getFocusableElements() {
      if (!this.contentEl) return [];
      const selector =
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), video[controls], [tabindex]:not([tabindex="-1"])';
      return Array.from(this.contentEl.querySelectorAll(selector)).filter((el) => {
        const isVisible = el.getClientRects().length > 0;
        return isVisible && !el.hasAttribute("hidden") && el.getAttribute("aria-hidden") !== "true";
      });
    }

    handleKeydown(e) {
      if (e.key === "Escape" || e.key === "Esc") {
        this.handleClose();
        return;
      }

      if (e.key !== "Tab" || !this.contentEl || this.contentEl.style.display === "none") {
        return;
      }
      this.lastTabDirection = e.shiftKey ? "backward" : "forward";

      const focusableEls = this.getFocusableElements();
      if (focusableEls.length === 0) {
        e.preventDefault();
        this.contentEl.focus();
        return;
      }

      const firstEl = focusableEls[0];
      const lastEl = focusableEls[focusableEls.length - 1];
      const activeEl = document.activeElement;
      const eventTarget = e.target;
      const isVideoControlFlow =
        activeEl instanceof HTMLVideoElement ||
        eventTarget instanceof HTMLVideoElement ||
        (eventTarget instanceof Element && !!eventTarget.closest("video[controls]"));

      // 让浏览器原生处理 video controls 内部的焦点链路（播放/音量/进度等）
      if (isVideoControlFlow) {
        return;
      }

      if (e.shiftKey) {
        if (activeEl === firstEl) {
          e.preventDefault();
          lastEl.focus();
        }
      } else if (activeEl === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    }

    handleFocusIn(e) {
      if (!this.contentEl || this.contentEl.style.display === "none") return;

      const target = e.target;
      if (!(target instanceof Node)) return;
      if (this.contentEl.contains(target)) return;

      const focusableEls = this.getFocusableElements();
      if (focusableEls.length === 0) {
        this.contentEl.focus();
        return;
      }

      if (this.lastTabDirection === "backward") {
        focusableEls[focusableEls.length - 1].focus();
      } else {
        focusableEls[0].focus();
      }
    }

    focusModal() {
      if (!this.contentEl) return;

      const focusable = this.getFocusableElements()[0];

      if (focusable && typeof focusable.focus === "function") {
        focusable.focus();
        return;
      }

      this.contentEl.focus();
    }

    stopPropagation(e) {
      e.stopPropagation();
    }
  }

  customElements.define("full-screen-modal", FullScreenModal);
});
